import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { AuthError, api, type Me } from "../api/client.js";
import { store, stored, useMediaQuery } from "./browser.js";
import { AnnounceContext, PortalContext, type LiftSort, type PortalContextValue, type PortalUI } from "./context.js";
import { currentWeeks, errorMessage, invalidate, liftsKey, loadLifts, loadNotes, loadWeek, loadWeeks, notesKey, prefetchAround, useResource, weekKey, weeksKey } from "./data.js";
import { listPlans } from "../builder/api.js";
import { noteItems } from "./model/notes.js";
import { Palette, type PaletteItem } from "../kit/Palette.js";
import { addDays, mondayOf, todayString, weekRange } from "./model/days.js";
import { Icon } from "./parts.js";
import { href, parseRoute, sheetURL, type View } from "./paths.js";
import { SheetLink } from "./SheetLink.js";
import { usePrefs } from "./prefs.js";
import { isMac } from "../builder/components/ui.js";
import { TourProvider, useTour } from "../tour/Tour.js";
import { TourNotice } from "../tour/TourNotice.js";
import { PhoneMenu } from "../builder/phone/PhoneMenu.js";
import { LiveNavItem } from "./views/Live.js";
import type { TourContext } from "../tour/steps.js";

const WIDE = "(min-width: 1100px)";
const SIDEBAR = 104;
const PAGE_MIN = 420;

function initialUI(): PortalUI {
  return { planColumn: stored("portal-column"), lift: {}, liftQuery: "", liftColumn: null, liftSort: "heaviest" as LiftSort, athletesExpanded: false };
}

export function PortalApp({ me, onSignedOut, onMe }: { me: Me; onSignedOut: () => void; onMe: (me: Me) => void }) {
  const announce = useContext(AnnounceContext);
  const location = useLocation();
  const navigate = useNavigate();
  const route = parseRoute(location.pathname, todayString());
  const onWeekPage = route.view !== null;
  // The Plan page needs only `me` (DESIGN §26.1): it shows while the week's model is still on its way.
  const needsModel = onWeekPage && route.view !== "plan";

  const weeks = useResource(weeksKey, loadWeeks);
  const week = useResource(weekKey(route.week), () => loadWeek(route.week));
  const model = week.value ?? null;

  const [ui, setUIState] = useState(initialUI);
  const setUI = useCallback((change: Partial<PortalUI>) => {
    setUIState((current) => ({ ...current, ...change }));
    if (change.planColumn != null) store("portal-column", change.planColumn);
  }, []);
  const pickLift = useCallback((athleteID: string, key: string) => setUIState((u) => ({ ...u, lift: { ...u.lift, [athleteID]: key } })), []);

  // MARK: The coach's sheet beside the page: wide windows only, and it follows the week.
  const sideBySide = useMediaQuery(WIDE);
  const [sheetOpen, setSheetOpenState] = useState(() => stored("portal-sheet") === "1");
  const [sheetWidth, setSheetWidthState] = useState<number | null>(() => {
    const width = Number(stored("portal-sheet-width"));
    return width > 0 ? width : null;
  });
  const [sheetWeekOverride, setSheetWeekOverride] = useState<string | null>(null);
  const [resizing, setResizing] = useState(false);
  const appRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const sheetButtonRef = useRef<HTMLAnchorElement>(null);
  const lastSheetWeek = useRef<string | null>(null);
  if (model) lastSheetWeek.current = model.document?.week ?? null;
  const panelWeek = sheetWeekOverride ?? lastSheetWeek.current;
  // A detail panel takes the right side; the sheet steps aside while one is open and comes back after.
  const detailOpen = route.view === "day" || route.view === "session" || route.view === "track" || ((route.view === "athlete" || route.view === "lift") && !!route.id);
  const panelShown = sheetOpen && sideBySide && onWeekPage && !detailOpen;
  const [frameWeek, setFrameWeek] = useState<string | null>(null);
  useEffect(() => {
    if (panelShown && panelWeek) setFrameWeek(panelWeek);
  }, [panelShown, panelWeek]);

  const setSheetOpen = useCallback((open: boolean, override: string | null = null) => {
    setSheetOpenState(open);
    setSheetWeekOverride(override);
    store("portal-sheet", open ? "1" : "");
  }, []);

  const setSheetWidth = useCallback((px: number) => {
    const width = Math.round(Math.min(Math.max(px, 320), window.innerWidth - SIDEBAR - PAGE_MIN));
    setSheetWidthState(width);
    store("portal-sheet-width", String(width));
  }, []);

  useLayoutEffect(() => {
    if (sheetWidth != null) appRef.current?.style.setProperty("--sheet-width", sheetWidth + "px");
  }, [sheetWidth]);

  const openSheet = useCallback(
    (sheetWeek: string, toggle = false) => {
      if (!sideBySide) return false;
      if (toggle && sheetOpen) setSheetOpen(false);
      else setSheetOpen(true, sheetWeek);
      return true;
    },
    [sideBySide, sheetOpen, setSheetOpen],
  );

  const startResize = (event: ReactPointerEvent) => {
    event.preventDefault();
    setResizing(true);
    const move = (e: PointerEvent) => setSheetWidth(window.innerWidth - e.clientX - 10);
    const up = () => {
      setResizing(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const resizeKey = (e: KeyboardEvent) => {
    const current = sheetWidth || panelRef.current?.offsetWidth || 0;
    if (e.key === "ArrowLeft") {
      setSheetWidth(current + 40);
      e.preventDefault();
    }
    if (e.key === "ArrowRight") {
      setSheetWidth(current - 40);
      e.preventDefault();
    }
  };

  // MARK: Moving between pages: focus goes to the page, which starts at the top.
  // A panel opening or closing over the same table is not a new page: the panel takes focus, the table stays put.
  const mainRef = useRef<HTMLElement>(null);
  const lastPlace = useRef<string | null>(null);
  const focusPending = useRef(false);
  useEffect(() => {
    setSheetWeekOverride(null);
    const place = placeOf(location.pathname);
    const first = lastPlace.current === null;
    const same = lastPlace.current === place;
    lastPlace.current = place;
    if (first || same) return;
    focusPending.current = true;
  }, [location.pathname]);
  useEffect(() => {
    if (focusPending.current && (model || !needsModel)) {
      focusPending.current = false;
      mainRef.current?.focus({ preventScroll: true });
      window.scrollTo(0, 0);
    }
  });

  const retry = () => {
    focusPending.current = true;
    invalidate(weekKey(route.week), weeksKey);
  };

  const stepWeek = (days: number, to?: string) => {
    // A day or session panel belongs to its week; another week opens on its matrix.
    const view: View = route.view === "day" || route.view === "session" || route.view === "track" || route.view === "sessions" ? "overview" : (route.view ?? "overview");
    navigate(href(to ?? addDays(route.week, days), view));
  };

  const [signingOut, setSigningOut] = useState(false);
  const signOut = () => {
    setSigningOut(true);
    api("/web/api/signout", { method: "POST" }).then(onSignedOut, (err: unknown) => {
      setSigningOut(false);
      if (!(err instanceof AuthError)) announce(errorMessage(err));
    });
  };

  // Forward as far as the latest published week (or this one).
  const current = mondayOf(todayString());
  const list = weeks.value ?? [];
  const latest = list.reduce((m, w) => (w.week > m ? w.week : m), current);
  const weekNav = useMemo(
    () => ({
      monday: route.week,
      prev: () => stepWeek(-7),
      next: () => stepWeek(7),
      canNext: route.week < latest,
      go: (monday: string) => stepWeek(0, monday),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route.week, route.view, route.id, latest],
  );
  const sheetButton = model?.document ? (
    <SheetLink
      week={model.document.week}
      toggle
      id="sheet-btn"
      ref={sheetButtonRef}
      aria-controls="sheet-panel"
      aria-pressed={sheetOpen && sideBySide ? "true" : "false"}
      labelClass="btn-label"
    />
  ) : null;

  const prefs = usePrefs();
  const context = useMemo<PortalContextValue>(
    () => ({ me, setMe: onMe, week: route.week, model, ui, setUI, pickLift, sideBySide, openSheet, announce, weekNav, sheetButton }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, onMe, route.week, model, ui, setUI, pickLift, sideBySide, openSheet, announce, weekNav, model?.document?.week, sheetOpen, prefs.version],
  );

  const nav = (to: string, label: string, icon: ReactNode, isCurrent: boolean) => (
    <Link className="nav-item" to={to} title={label} aria-current={isCurrent ? "page" : undefined}>
      {icon}
      <span className="nav-label">{label}</span>
    </Link>
  );
  const v = route.view;
  const path = location.pathname;

  // ⌘K: lifts, notes and weeks are fetched when the palette first opens; athletes come with the week.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteWeeks, setPaletteWeeks] = useState<PaletteItem[]>([]);
  const lifts = useResource(paletteOpen ? liftsKey : null, loadLifts);
  const notes = useResource(paletteOpen ? notesKey : null, loadNotes);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        e.stopPropagation();
        flushSync(() => setPaletteOpen((o) => !o));
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  useEffect(() => {
    if (!paletteOpen || paletteWeeks.length) return;
    Promise.all([listPlans().catch(() => []), currentWeeks().catch(() => [])]).then(([plans, published]) => {
      const titles = new Map(plans.map((p) => [p.week, p.title]));
      const all = [...new Set([...plans.map((p) => p.week), ...published.map((w) => w.week)])].sort().reverse();
      setPaletteWeeks(all.map((w) => ({ id: "week:" + w, label: titles.get(w) || "Week of " + weekRange(w), sub: titles.get(w) ? weekRange(w) : undefined, kind: "Plan", to: `/w/${w}/plan` })));
    });
  }, [paletteOpen, paletteWeeks.length]);
  const paletteItems = useMemo<PaletteItem[]>(() => {
    const places: PaletteItem[] = [
      { id: "p:plan", label: "Plan", kind: "Page", to: href(route.week, "plan") },
      { id: "p:week", label: "Week", kind: "Page", to: href(route.week) },
      { id: "p:live", label: "Live", kind: "Page", to: "/live" },
      { id: "p:athletes", label: "Athletes", kind: "Page", to: "/athletes" },
      { id: "p:lifts", label: "Lifts", kind: "Page", to: "/lifts" },
      { id: "p:meets", label: "Meets", kind: "Page", to: "/meets" },
    ];
    if (me.person.canPlan || me.person.isAdmin) places.push({ id: "p:groups", label: "Groups", kind: "Page", to: me.person.canPlan ? "/?groups=1" : "/groups" });
    if (me.person.isAdmin) places.push({ id: "p:team", label: "Team", kind: "Page", to: "/team" });
    places.push({ id: "p:settings", label: "Settings", kind: "Page", to: "/settings" });
    const athletes = (model?.athletes ?? []).map((a) => ({ id: "a:" + a.id, label: a.name, sub: a.column ?? undefined, kind: "Athlete", to: `/athlete/${encodeURIComponent(a.id)}` }));
    const liftItems = (lifts.value?.list ?? []).map((l) => ({ id: "l:" + l.key, label: l.name, kind: "Lift", to: `/lift/${encodeURIComponent(l.key)}` }));
    const notesFound = noteItems(notes.value, model?.athletes ?? []).map((n) => ({ id: n.id, label: n.label, sub: n.sub, kind: "Note", to: `/athlete/${encodeURIComponent(n.athleteID)}` }));
    return [...places, ...athletes, ...paletteWeeks, ...liftItems, ...notesFound];
  }, [route.week, me, model, lifts.value, notes.value, paletteWeeks]);

  // Once the Plan page has its week, the weeks either side are read ahead for the arrows.
  const onPlan = route.view === "plan";
  const modelReady = !!model;
  useEffect(() => {
    if (!onPlan || !modelReady) return;
    const t = setTimeout(() => prefetchAround(route.week), 0);
    return () => clearTimeout(t);
  }, [onPlan, modelReady, route.week]);

  const phone = useMediaQuery("(max-width: 860px)");
  const tourContext = useMemo<TourContext>(() => ({ me, week: route.week, athletes: model?.athletes ?? [], phone }), [me, route.week, model, phone]);

  return (
    <PortalContext.Provider value={context}>
      <TourProvider context={tourContext}>
        <TourPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />
        <a className="skip" href="#view">
          Skip to content
        </a>
        <div className={"app is-rail" + (panelShown ? " with-sheet" : "") + (phone ? " is-phone" : "") + (phone && route.view === "plan" && me.person.canPlan ? " is-phone-plan" : "")} ref={appRef}>
          <nav className="sidebar glass" aria-label="Sections">
            <Link className="rail-logo" to="/" aria-label="Training: this week" title="Training">
              <img src="/favicon.png" alt="" width={28} height={28} />
            </Link>
            <button type="button" className="nav-item rail-search" aria-label="Go to" title={`Go to… (${isMac ? "⌘K" : "Ctrl+K"})`} aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"} onClick={() => setPaletteOpen(true)}>
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <path d="m13.2 13.2 3.8 3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <span className="nav-label">
              <kbd className="rail-kbd">{isMac ? "⌘K" : "Ctrl K"}</kbd>
            </span>
            </button>
            <div className="nav-group places">
              {nav(href(route.week, "plan"), "Plan", Icon.plan, v === "plan")}
              {nav(href(route.week), "Week", Icon.week, v === "overview" || v === "sessions" || v === "day" || v === "session" || v === "track")}
              <LiveNavItem current={path === "/live"} />
              {nav(href(route.week, "athletes"), "Athletes", Icon.team, v === "athletes" || v === "athlete")}
              {nav(href(route.week, "lifts"), "Lifts", Icon.lifts, v === "lifts" || v === "lift")}
              {nav("/meets", "Meets", Icon.meets, path === "/meets" || path.startsWith("/meet/"))}
            </div>
            <AccountMenu me={me} signingOut={signingOut} onSignOut={signOut} current={path} />
          </nav>
          <div className="main">
            <main className="content" id="view" tabIndex={-1} ref={mainRef} aria-busy={needsModel && week.loading ? true : undefined}>
              {phone && !(v === "plan" && me.person.canPlan) ? (
                <div className="wp-pagebar">
                  <PhoneMenu me={me} monday={route.week ?? current} onSignOut={signOut} current={placeName(v, path)} onSearch={() => setPaletteOpen(true)} />
                </div>
              ) : null}
              <TourNotice />
              {!needsModel || model ? (
                <Outlet />
              ) : week.error ? (
                <div className="card empty" role="alert">
                  <p>{errorMessage(week.error)}</p>
                  <button type="button" className="btn" onClick={retry}>
                    Try again
                  </button>
                </div>
              ) : (
                <p className="empty" role="status">
                  Loading the week…
                </p>
              )}
            </main>
          </div>
          <aside className={"sheet-panel" + (resizing ? " resizing" : "")} id="sheet-panel" aria-labelledby="sheet-title" hidden={!panelShown} ref={panelRef}>
            <div
              className="sheet-resize"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the sheet"
              aria-valuenow={sheetWidth ?? undefined}
              aria-valuemin={320}
              tabIndex={0}
              onPointerDown={startResize}
              onKeyDown={resizeKey}
            />
            <div className="sheet-head">
              <h2 id="sheet-title">{panelWeek ? "Coach's sheet · " + weekRange(panelWeek) : "Coach's sheet"}</h2>
              {panelWeek ? (
                <a className="btn" href={sheetURL(panelWeek)} target="_blank" rel="noopener">
                  Open<span className="sr"> the sheet in a new tab</span>
                </a>
              ) : null}
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setSheetOpen(false);
                  sheetButtonRef.current?.focus();
                }}
              >
                Close<span className="sr"> the sheet</span>
              </button>
            </div>
            {frameWeek ? (
              // Fitted to the panel's width, no thumbnails. Its src only changes with the week, so the reader keeps its page and zoom.
              <iframe id="sheet-frame" title="Coach's sheet" hidden={!panelWeek} src={sheetURL(frameWeek) + "#navpanes=0&view=FitH"} />
            ) : null}
            <p className="card empty" hidden={!!panelWeek}>
              No sheet for this week.
            </p>
          </aside>
        </div>
      </TourProvider>
    </PortalContext.Provider>
  );
}

/** ⌘K with the tour's chapters in it. */
function TourPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: PaletteItem[] }) {
  const tour = useTour();
  const all = useMemo<PaletteItem[]>(
    () => [
      ...items,
      { id: "tour:all", label: "Take the tour", kind: "Tour", run: () => tour.start("all") },
      ...tour.chapters.map((c) => ({ id: "tour:" + c.id, label: "Tour: " + c.title, kind: "Tour", run: () => tour.start(c.id) })),
    ],
    [items, tour],
  );
  return <Palette open={open} onClose={onClose} items={all} />;
}

/** Who is signed in, at the foot of the rail: what only some people use (Groups, Team), and Sign out. */
function AccountMenu({ me, signingOut, onSignOut, current }: { me: Me; signingOut: boolean; onSignOut: () => void; current: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>("a, button")?.focus();
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const id = setTimeout(() => window.addEventListener("pointerdown", away), 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("pointerdown", away);
    };
  }, [open]);
  useEffect(() => setOpen(false), [current]);
  const initials = me.person.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  const items: { to?: string; label: string; icon: ReactNode; run?: () => void }[] = [];
  // The planner edits groups beside the plan; the admin who doesn't plan has them as a page.
  if (me.person.canPlan) items.push({ to: "/?groups=1", label: "Groups", icon: Icon.groups });
  else if (me.person.isAdmin) items.push({ to: "/groups", label: "Groups", icon: Icon.groups });
  if (me.person.isAdmin) items.push({ to: "/team", label: "Team", icon: Icon.team });
  items.push({ to: "/settings", label: "Settings", icon: Icon.settings });
  return (
    <div className="account" ref={wrap}>
      <button ref={button} type="button" className="account-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} title={me.person.name}>
        <span className="avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="nav-label">{me.person.name.split(/\s+/)[0]}</span>
      </button>
      {open ? (
        <div
          ref={menu}
          className="account-menu"
          role="menu"
          aria-label={me.person.name}
          onKeyDown={(e) => {
            const items = Array.from(menu.current?.querySelectorAll<HTMLElement>("a, button") ?? []);
            const i = items.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              button.current?.focus();
            }
          }}
        >
          <div className="account-who">
            <span className="account-name">{me.person.name}</span>
            <span className="account-role">{me.person.isAdmin ? "Admin" : me.person.canPlan ? "Plans the team" : "Coach"}</span>
          </div>
          {items.map((item) => (
            <Link key={item.label} role="menuitem" className="account-item" to={item.to!}>
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
          <button type="button" role="menuitem" className="account-item" disabled={signingOut} onClick={onSignOut}>
            <span>Sign out</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The table a path shows, whether or not a panel is open over it. */
/** The page's name for the phone's `Name ▾` menu: the rail's places. */
function placeName(view: string | null, path: string): string {
  if (view === "plan") return "Plan";
  if (view === "athletes" || view === "athlete") return "Athletes";
  if (view === "lifts" || view === "lift") return "Lifts";
  if (path.startsWith("/live")) return "Live";
  if (path === "/meets" || path.startsWith("/meet/")) return "Meets";
  if (path.startsWith("/groups")) return "Groups";
  if (path.startsWith("/settings")) return "Settings";
  return "Week";
}

function placeOf(pathname: string): string {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (parts[0] === "athlete" || parts[0] === "athletes") return "athletes";
  if (parts[0] === "lift" || parts[0] === "lifts") return "lifts";
  if (parts[0] === "meet" || parts[0] === "meets") return "meets";
  if (parts[0] === "w" && ["day", "session", "track", undefined].includes(parts[2])) return "week:" + parts[1];
  return pathname;
}
